// src/components/properties/RegistrationButton.jsx
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRegistered } from "@fortawesome/free-solid-svg-icons";
import { isOnlineOrZeus, isRegistrationOpen, getRegistrationStatus } from "../../utilities/hybridSales";

/**
 * Renders one of four registration button variants depending on the
 * property's auction type:
 *   1. Hybrid sale  → "Online Reg / In-Person Sale" (links to zeusauction.com)
 *   2. Online sale  → "Register: Zeus"              (links to zeusauction.com)
 *   3. Foreclosure without Zeus link → null (no registration available)
 *   4. Standard     → toggleable Register / Unregister button
 *
 * All buttons use a stacked icon-on-top, text-on-bottom layout for uniform height.
 *
 * @param {object}        property
 * @param {boolean}       isRegistered
 * @param {number|string} standardizedSaleId
 * @param {number|string} effectiveSaleId     - real auction sale ID for foreclosures
 * @param {boolean}       isFetchingInfo  - true while propertyInfo is loading
 * @param {object}        propertyInfo    - truthy once card details have loaded
 * @param {function}      onRegisterClick - called with { saleId, auction }
 * @param {function}      onError         - called with an error message string
 */

const btnContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px",
};

const RegistrationButton = ({
  property,
  isRegistered,
  standardizedSaleId,
  effectiveSaleId,
  auctionDetail,
  isFetchingInfo,
  propertyInfo,
  onRegisterClick,
  onError,
}) => {
  const isHybrid = isOnlineOrZeus(property) === "Online Registration/In-Person Sale";
  const isOnlineAuction =
    property.type === "Online" ||
    (property.auctionLocation && property.auctionLocation.includes("www.zeusauction.com"));

  if (isHybrid) {
    const registrationStatus = getRegistrationStatus(auctionDetail);
    const registrationOpen = isRegistrationOpen(auctionDetail);

    const tooltip =
      registrationStatus === "closed" ? (
        <Tooltip>Registration for this sale has closed.</Tooltip>
      ) : registrationStatus === "not_open" ? (
        <Tooltip>Registration for this sale has not yet opened.</Tooltip>
      ) : (
        <span />
      );

    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        <span className="unique-auction-button-wrapper flex-fill">
          <Button
            variant="outline-primary"
            href="http://www.zeusauction.com"
            target="_blank"
            rel="noopener noreferrer"
            className="unique-auction-action-button unique-auction-register-zeus-button btn-sm w-100"
            disabled={!registrationOpen}
            aria-label="Register online for in-person sale via Zeus"
          >
            <div style={btnContentStyle}>
              <FontAwesomeIcon icon={faRegistered} />
              <span>Online Reg/</span>
              <span>In-Person Sale</span>
            </div>
          </Button>
        </span>
      </OverlayTrigger>
    );
  }

  if (isOnlineAuction) {
    return (
      <Button
        className="flex-fill"
        variant="outline-primary"
        href="http://www.zeusauction.com"
        target="_blank"
        rel="noopener noreferrer"
        disabled={!isRegistrationOpen(property.auctionDetail)}
        aria-label="Register for Zeus online auction"
      >
        <div style={btnContentStyle}>
          <FontAwesomeIcon icon={faRegistered} />
          <span>Register: Zeus</span>
        </div>
      </Button>
    );
  }

  // Foreclosure sales without a Zeus link cannot pre-register
  if (property.saleType === "F") {
    return null;
  }

  return (
    <Button
      className="flex-fill"
      variant={isRegistered ? "outline-danger" : "outline-primary"}
      onClick={() => {
        const saleIdToUse = effectiveSaleId ?? standardizedSaleId;
        if (saleIdToUse === null || saleIdToUse === undefined || isNaN(Number(saleIdToUse))) {
          onError("Cannot process your request due to an invalid sale ID.");
          return;
        }
        onRegisterClick({ saleId: saleIdToUse, auction: property });
      }}
      disabled={isFetchingInfo || !propertyInfo}
      aria-label={isRegistered ? "Unregister from this sale" : "Register for this sale"}
    >
      <div style={btnContentStyle}>
        <FontAwesomeIcon icon={faRegistered} />
        <span>{isRegistered ? "Unregister" : "Register"}</span>
      </div>
    </Button>
  );
};

export default RegistrationButton;